import axios from 'axios';
import Util from '../utils/Util';
import authUtil from '../utils/AuthUtils';
import { getServiceApiKey } from '../utils/apiKey';

const BASE_API_URL = window?.appConfig?.REACT_APP_API_ENDPOINT;

const DEFAULT_CLIENT_NAME = 'Locations-Service-UI';
const USE_MOCK_BULK_UPLOADS = false;
const USE_MOCK_BULK_UPLOAD_FILTERS = false;
const USE_MOCK_BULK_UPLOAD_DOWNLOAD = false;

// Helper function to create config
const getConfig = async () => {
  const token = await authUtil.getLocationServiceToken();
  let userEmail = '';
  try {
    const userDetails = JSON.parse(localStorage.getItem('userDetails') || '{}');
    if (userDetails && typeof userDetails.emailAddress === 'string') {
      userEmail = userDetails.emailAddress;
    }
  } catch (error) {
    console.warn('Could not parse user details from localStorage:', error);
  }

  const headers = {
    "x-api-key": getServiceApiKey(),
    'x-location-service-integration-token': token,
  };

  if (DEFAULT_CLIENT_NAME) {
    headers['X-Client-Name'] = DEFAULT_CLIENT_NAME;
  }

  headers['X-User-Email'] = userEmail || '';

  return { headers };
};

async function downloadLocationsData(query) {
  const config = await getConfig();
  try {
    const userDetails = JSON.parse(localStorage.getItem('userDetails') || '{}');
    if (userDetails.emailAddress) {
      query['_recipientEmails'] = userDetails.emailAddress;
      let queryParams = Util.buildQueryParams(query);
      return axios.get(BASE_API_URL + `/v3/locations` + queryParams, {
        responseType: 'blob',
        headers: {
          ...config.headers,
          'X-LS-FieldMask': '*',
        },
      });
    } else {
      return { data: null };
    }
  } catch (parseError) {
    console.warn('Could not parse user details from localStorage:', parseError);
    // Continue without email if parsing fails
  }
}

async function downloadZonesData(query) {
  const config = await getConfig();
  let queryParams = Util.buildQueryParams(query);
  return axios.get(BASE_API_URL + `/v2/zones` + queryParams, {
    responseType: 'blob',
    headers: {
      ...config.headers,
    }
  });
}

async function downloadAddressesData(query) {
  const config = await getConfig();
  let queryParams = Util.buildQueryParams(query);
  return axios.get(BASE_API_URL + `/v4/addresses/search` + queryParams, {
    responseType: 'blob',
    headers: {
      ...config.headers,
    }
  });
}

async function downloadCountriesData(query) {
  const config = await getConfig();
  let queryParams = Util.buildQueryParams(query);
  return axios.get(BASE_API_URL + `/v3/countries` + queryParams, {
    responseType: 'blob',
    headers: {
      ...config.headers,
    }
  });
}

async function downloadStatesData(query) {
  const config = await getConfig();
  if (!query._limit) {
    query._limit = 10000;
  }
  let queryParams = Util.buildQueryParams(query);
  return axios.get(BASE_API_URL + `/v2/states` + queryParams, {
    responseType: 'blob',
    headers: {
      ...config.headers,
    }
  });
}

async function downloadCitiesData(query) {
  if (!query._limit) {
    query._limit = 30000;
  }
  const config = await getConfig();
  let queryParams = Util.buildQueryParams(query);
  return await axios.get(BASE_API_URL + `/v2/cities` + queryParams, {
    responseType: 'blob',
    headers: {
      ...config.headers,
    }
  });
}

async function downloadUnlocodeData(query) {
  if (query._limit == undefined) {
    query._limit = 10000;
  }
  const config = await getConfig();
  config.headers['X-LS-FieldMask'] = 'code,name,type,countryName,countryIso2'
  let queryParams = Util.buildQueryParams(query);
  return await axios.get(BASE_API_URL + `/v2/unlocodes` + queryParams, {
    responseType: 'blob',
    headers: {
      ...config.headers,
    }
  });
}

async function getLocationsData(query) {
  const config = await getConfig();
  let queryParams = Util.buildQueryParams(query);
  return axios.get(BASE_API_URL + `/v3/locations` + queryParams, {
    headers: {
      ...config.headers,
      'X-LS-FieldMask': '*',
    },
  });
}

async function getAddressData(query) {
  const config = await getConfig();
  let queryParams = Util.buildQueryParams(query);
  return axios.get(BASE_API_URL + `/v4/addresses/search` + queryParams, config);
}

async function getAddressesV4(addressIds, { applicationNamespace } = {}) {
  const config = await getConfig();
  const params = { addressIds: addressIds.join(',') };
  if (applicationNamespace) {
    params.applicationNamespace = applicationNamespace;
  }
  const queryParams = Util.buildQueryParams(params);
  return axios.get(BASE_API_URL + `/v4/addresses` + queryParams, config);
}


async function getZonesData(query) {
  const config = await getConfig();
  let queryParams = Util.buildQueryParams(query);
  return axios.get(BASE_API_URL + `/v2/zones` + queryParams, config);
}

async function getCountriesData() {
  const config = await getConfig();
  return axios.get(BASE_API_URL + `/v3/countries`, config);
}

async function getStatesData(query) {
  const config = await getConfig();
  if (!query._limit) {
    query._limit = 10000;
  }
  let queryParams = Util.buildQueryParams(query);
  return axios.get(BASE_API_URL + `/v2/states` + queryParams, config);
}

async function getCitiesData(query) {
  if (!query._limit) {
    query._limit = 30000;
  }
  const config = await getConfig();
  let queryParams = Util.buildQueryParams(query);
  return await axios.get(BASE_API_URL + `/v2/cities` + queryParams, config);
}

async function getUnlocodeData(query) {
  if (query._limit == undefined || query['X-LS-FieldMask'] == null) {
    query._limit = 10000;
  }
  const config = await getConfig();
  config.headers['X-LS-FieldMask'] = 'code,name,type,countryName,countryIso2'
  let queryParams = Util.buildQueryParams(query);
  return await axios.get(BASE_API_URL + `/v2/unlocodes` + queryParams, config);
}

async function createLocation(data) {
  try {
    const config = await getConfig();
    data['checkWithNamesMatch'] = true;

    // Add email from SSO login if available
    try {
      const userDetails = JSON.parse(localStorage.getItem('userDetails') || '{}');
      if (userDetails.emailAddress) {
        data['emails'] = userDetails.emailAddress;
      }
    } catch (parseError) {
      console.warn('Could not parse user details from localStorage:', parseError);
      // Continue without email if parsing fails
    }

    const response = await axios.post(BASE_API_URL + `/v2/locations`, data, config);
    return response; // let the consumer handle it
  } catch (error) {
    // extract backend message if available
    if (error.response && error.response.data) {
      const errData = error.response.data;
      const message =
        errData.errorMessage ||
        errData.error ||
        JSON.stringify(errData) ||
        'Unknown server error';
      throw new Error(message);
    }

    throw new Error('Network or unexpected error');
  }
}

// Bulk upload functions
async function bulkUploadUnlocodes(file, emails = '') {
  try {
    const config = await getConfig();
    const formData = new FormData();
    formData.append('unlocodeFile', file);
    formData.append('emails', emails);

    const response = await axios.post(BASE_API_URL + `/v2/unlocodes`, formData, {
      ...config,
      headers: {
        ...config.headers,
        'Content-Type': 'multipart/form-data',
      },
    });
    return response;
  } catch (error) {
    if (error.response && error.response.data) {
      const errData = error.response.data;
      const message =
        errData.errorMessage ||
        errData.error ||
        JSON.stringify(errData) ||
        'Unknown server error';
      throw new Error(message);
    }
    throw new Error('Network or unexpected error');
  }
}

async function bulkUploadStates(file, emails = '') {
  try {
    const config = await getConfig();
    const formData = new FormData();
    formData.append('statesFile', file);
    formData.append('emails', emails);

    const response = await axios.post(BASE_API_URL + `/v2/states`, formData, {
      ...config,
      headers: {
        ...config.headers,
        'Content-Type': 'multipart/form-data',
      },
    });
    return response;
  } catch (error) {
    if (error.response && error.response.data) {
      const errData = error.response.data;
      const message =
        errData.errorMessage ||
        errData.error ||
        JSON.stringify(errData) ||
        'Unknown server error';
      throw new Error(message);
    }
    throw new Error('Network or unexpected error');
  }
}

async function bulkUploadCities(file, emails = '') {
  try {
    const config = await getConfig();
    const formData = new FormData();
    formData.append('citiesFile', file);
    formData.append('emails', emails);

    const response = await axios.post(BASE_API_URL + `/v2/cities`, formData, {
      ...config,
      headers: {
        ...config.headers,
        'Content-Type': 'multipart/form-data',
      },
    });
    return response;
  } catch (error) {
    if (error.response && error.response.data) {
      const errData = error.response.data;
      const message =
        errData.errorMessage ||
        errData.error ||
        JSON.stringify(errData) ||
        'Unknown server error';
      throw new Error(message);
    }
    throw new Error('Network or unexpected error');
  }
}

async function bulkUploadLocations(
  file,
  applicationNamespace,
  checkWithNamesMatch = false,
  checkWithGeocodeMatch = false,
  skipLocodeValidation = false,
  isCreateStaticData = false,
  isExtData = false,
  batchSize = 1000,
  emails = '',
  podId = ''
) {
  const config = await getConfig();
  const formData = new FormData();
  formData.append('locationsFile', file);
  formData.append('applicationNamespace', applicationNamespace);
  formData.append('checkWithNamesMatch', checkWithNamesMatch.toString());
  formData.append('checkWithGeocodeMatch', checkWithGeocodeMatch.toString());
  formData.append('skipLocodeValidation', skipLocodeValidation.toString());
  formData.append('isCreateStaticData', isCreateStaticData.toString());
  formData.append('isExtData', isExtData.toString());
  formData.append('batchSize', batchSize);
  formData.append('emails', emails);

  if (podId && podId.trim() !== '') {
    formData.append('podId', podId.trim());
  }

  const makeRequest = () =>
    axios.post(BASE_API_URL + `/v2/locations/upload`, formData, {
      ...config,
      headers: {
        ...config.headers,
        'Content-Type': 'multipart/form-data',
      },
    });

  // If no podId, do a single call with existing error handling
  if (!podId || podId.trim() === '') {
    try {
      const response = await makeRequest();
      return response;
    } catch (error) {
      if (error.response && error.response.data) {
        const errData = error.response.data;
        const message =
          errData.errorMessage ||
          errData.error ||
          JSON.stringify(errData) ||
          'Unknown server error';
        throw new Error(message);
      }
      throw new Error('Network or unexpected error');
    }
  } else {
    // With podId: retry on specific error (40030) up to 20 times
    const maxRetries = 20;

    for (let attempt = 1; attempt <= maxRetries; attempt++) {
      try {
        const response = await makeRequest();
        return response;
      } catch (error) {
        if (error.response && error.response.data) {
          const errData = error.response.data;
          const errorCode = errData.errorCode;
          const errorMessage = errData.errorMessage;

          if (errorCode === '40030' && errorMessage === 'Picked up by wrong pod for processing') {
            if (attempt < maxRetries) {
              // simple linear backoff
              // await new Promise(resolve => setTimeout(resolve, 1000 * attempt));
              continue;
            }
            // After max retries
            throw new Error('Pod Id not present');
          }else{
            const message =
              errData.errorMessage ||
              errData.error ||
              JSON.stringify(errData) ||
              'Unknown server error';
              
            throw new Error(message);
          }
        }
        // No structured error response – treat as network/unexpected
        throw new Error('Network or unexpected error');
      }
    }
    throw new Error('Pod Id not present');
  }
}

/**
 * Get Job Tracking Data (external test API - always same endpoint)
 * Returns array of job tracking objects.
 */
async function getJobTrackingData(query = { _limit: 10 }) {
  const config = await getConfig();
  let queryParams = Util.buildQueryParams(query);
  return axios.get(BASE_API_URL + '/v2/job' + queryParams, config);
}

async function bulkUploadLocationsV2(file, applicationNamespace, emails = '', priority = 'NORMAL') {
  try {
    const config = await getConfig();
    const formData = new FormData();
    formData.append('locationsFile', file);
    formData.append('applicationNamespace', applicationNamespace);
    formData.append('emails', emails);
    formData.append('priority', priority.toUpperCase());

    const response = await axios.post(BASE_API_URL + `/v2/upload/locations`, formData, {
      ...config,
      headers: {
        ...config.headers,
        'Content-Type': 'multipart/form-data',
      },
    });
    return response;
  } catch (error) {
    if (error.response && error.response.data) {
      const errData = error.response.data;
      const message =
        errData.errorMessage ||
        errData.error ||
        JSON.stringify(errData) ||
        'Unknown server error';
      throw new Error(message);
    }
    throw new Error('Network or unexpected error');
  }
}

// Unified upload function for all entity types
async function bulkUploadEntityV3(entityType, file, applicationNamespace = '', emails = '', priority = 'NORMAL', locationOptions = {}) {
  try {
    const config = await getConfig();
    const formData = new FormData();

    // Map entity types to file field names
    const fileFieldNames = {
      'locations': 'locationsFile',
      'cities': 'citiesFile',
      'states': 'statesFile',
      'unlocodes': 'unlocodeFile'
    };

    const fileFieldName = fileFieldNames[entityType] || 'locationsFile';
    formData.append(fileFieldName, file);
    formData.append('emails', emails);

    if (entityType === 'locations') {
      if (applicationNamespace) {
        formData.append('applicationNamespace', applicationNamespace);
      }
      if (priority) {
        formData.append('priority', priority.toUpperCase());
      }

      const {
        checkWithNamesMatch = false,
        checkWithGeocodeMatch = false,
        skipLocodeValidation = false,
        isCreateStaticData = false
      } = locationOptions;

      formData.append('checkWithNamesMatch', checkWithNamesMatch.toString());
      formData.append('checkWithGeocodeMatch', checkWithGeocodeMatch.toString());
      formData.append('skipLocodeValidation', skipLocodeValidation.toString());
      formData.append('isCreateStaticData', isCreateStaticData.toString());
    } 
    const response = await axios.post(BASE_API_URL + `/v2/upload/${entityType}`, formData, {
      ...config,
      headers: {
        ...config.headers,
        'Content-Type': 'multipart/form-data',
      },
    });
    return response;
  } catch (error) {
    if (error.response && error.response.data) {
      const errData = error.response.data;
      const message =
        errData.errorMessage ||
        errData.error ||
        JSON.stringify(errData) ||
        'Unknown server error';
      throw new Error(message);
    }
    throw new Error('Network or unexpected error');
  }
}

// New bulk upload management functions
async function getBulkUploads(query) {
  try {
    const config = await getConfig();
    const queryParams = Util.buildQueryParams(query);

    if (USE_MOCK_BULK_UPLOADS) {
      return new Promise((resolve) => {
        setTimeout(() => {
          const mockData = generateMockBulkUploads(query);
          const mockResponse = {
            data: mockData.data,
            headers: {
              'x-total-count': String(mockData.totalCount)
            }
          };
          resolve(mockResponse);
        }, 500);
      });
    }

    const response = await axios.get(BASE_API_URL + `/v2/bulk-uploads` + queryParams, config);
    console.debug('Bulk uploads API response', {
      status: response.status,
      totalCount: response.headers?.['x-total-count'],
      sample: Array.isArray(response.data) ? response.data.slice(0, 3) : response.data
    });
    return response;
  } catch (error) {
    if (error.response && error.response.data) {
      const errData = error.response.data;
      const message =
        errData.errorMessage ||
        errData.error ||
        JSON.stringify(errData) ||
        'Unknown server error';
      throw new Error(message);
    }
    throw new Error('Network or unexpected error');
  }
}

// Mock data generator for development
function generateMockBulkUploads(query) {
  const statuses = ['Completed', 'Pending', 'In Review'];
  const types = ['Normal', 'Emergency'];
  const uploadTypes = ['location', 'state', 'city', 'unlocode'];
  const namespaces = ['namespace1', 'namespace2', 'namespace3', 'namespace4'];
  const reviewers = ['Drew Weber', 'Portia Howe', 'John Smith', 'Alice Johnson'];

  // Generate mock data
  const allUploads = [];
  for (let i = 1; i <= 90; i++) {
    const status = statuses[Math.floor(Math.random() * statuses.length)];
    const type = types[Math.floor(Math.random() * types.length)];
    const uploadType = uploadTypes[Math.floor(Math.random() * uploadTypes.length)];
    const namespace = namespaces[Math.floor(Math.random() * namespaces.length)];
    const reviewer = reviewers[Math.floor(Math.random() * reviewers.length)];

    const date = new Date();
    date.setDate(date.getDate() - Math.floor(Math.random() * 30));
    date.setHours(Math.floor(Math.random() * 24));
    date.setMinutes(Math.floor(Math.random() * 60));

    allUploads.push({
      uploadId: 700000 + i,
      fileName: `Location Chennai South${i > 1 ? ` (${i})` : ''}.xls`,
      uploadedDate: date.toISOString(),
      uploadedBy: `user${i}@email.com`,
      type: type,
      uploadType: uploadType,
      insertedRow: Math.floor(Math.random() * 200),
      errorRow: Math.floor(Math.random() * 10),
      reviewer: reviewer,
      status: status,
      applicationNamespace: namespace
    });
  }

  // Apply filters
  let filtered = [...allUploads];

  if (query.status) {
    filtered = filtered.filter(u => u.status === query.status);
  }
  if (query.type) {
    filtered = filtered.filter(u => u.type === query.type);
  }
  if (query.namespace) {
    filtered = filtered.filter(u => u.applicationNamespace === query.namespace);
  }
  if (query.uploadType) {
    filtered = filtered.filter(u => u.uploadType === query.uploadType);
  }

  // Apply sorting
  if (query._sort) {
    filtered.sort((a, b) => {
      let aVal = a[query._sort];
      let bVal = b[query._sort];

      if (query._sort === 'uploadedDate') {
        aVal = new Date(aVal).getTime();
        bVal = new Date(bVal).getTime();
      }

      if (typeof aVal === 'string') {
        aVal = aVal.toLowerCase();
        bVal = bVal.toLowerCase();
      }

      if (aVal < bVal) return query._order === 'asc' ? -1 : 1;
      if (aVal > bVal) return query._order === 'asc' ? 1 : -1;
      return 0;
    });
  }

  // Apply pagination
  const page = query._page || 1;
  const limit = query._limit || 10;
  const startIndex = (page - 1) * limit;
  const endIndex = startIndex + limit;
  const paginatedData = filtered.slice(startIndex, endIndex);

  return {
    data: paginatedData,
    totalCount: filtered.length
  };
}

async function getBulkUploadFilters() {
  try {
    const config = await getConfig();

    if (USE_MOCK_BULK_UPLOAD_FILTERS) {
      return new Promise((resolve) => {
        setTimeout(() => {
          resolve({
            data: {
              statuses: ['Completed', 'Pending', 'In Review'],
              types: ['Normal', 'Emergency'],
              namespaces: ['namespace1', 'namespace2', 'namespace3', 'namespace4']
            }
          });
        }, 300);
      });
    }

    const response = await axios.get(BASE_API_URL + `/v2/bulk-uploads/filters`, config);
    return response;
  } catch (error) {
    if (error.response && error.response.data) {
      const errData = error.response.data;
      const message =
        errData.errorMessage ||
        errData.error ||
        JSON.stringify(errData) ||
        'Unknown server error';
      throw new Error(message);
    }
    throw new Error('Network or unexpected error');
  }
}

async function downloadBulkUpload(uploadId, fileName = 'download.xlsx') {
  try {
    const config = await getConfig();

    if (USE_MOCK_BULK_UPLOAD_DOWNLOAD) {
      // Create a mock Excel file blob
      return new Promise((resolve) => {
        const mockData = 'Mock Excel File Content';
        const blob = new Blob([mockData], {
          type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
        });
        resolve({
          data: blob,
          headers: {
            'content-disposition': `attachment; filename="${fileName}"`
          }
        });
      });
    }

    const response = await axios.get(BASE_API_URL + `/v2/bulk-uploads/${uploadId}/download`, {
      ...config,
      responseType: 'blob'
    });
    return response;
  } catch (error) {
    if (error.response && error.response.data) {
      const errData = error.response.data;
      const message =
        errData.errorMessage ||
        errData.error ||
        JSON.stringify(errData) ||
        'Unknown server error';
      throw new Error(message);
    }
    throw new Error('Network or unexpected error');
  }
}

export default {
  getAddressData,
  getAddressesV4,
  getLocationsData,
  getZonesData,
  getCountriesData,
  getStatesData,
  getCitiesData,
  getUnlocodeData,
  downloadCountriesData,
  downloadStatesData,
  downloadCitiesData,
  downloadUnlocodeData,
  downloadLocationsData,
  downloadZonesData,
  downloadAddressesData,
  createLocation,
  bulkUploadUnlocodes,
  bulkUploadStates,
  bulkUploadCities,
  bulkUploadLocations,
  bulkUploadLocationsV2,
  bulkUploadEntityV3,
  getBulkUploads,
  getBulkUploadFilters,
  downloadBulkUpload,
  getJobTrackingData
};
